import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import PersonIcon from "@mui/icons-material/Person";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";

import { getMapMetaById, getMapImageUrl } from "../../constants/mapMeta";
import {
  MAP_VOTE_PENDING_HEADING,
  MAP_VOTE_PENDING_HINT,
} from "../../constants/mapVote";
import utils from "../../universal/utils";
import { colors } from "../../theme/colors";
import { DIALOG_ACCEPT_BUTTON_SX } from "../common/dialogActionButtonSx";

const backdropStyles = {
  backgroundColor: "rgba(4, 2, 14, 0.8)",
  backdropFilter: "blur(4px)",
};

const DOTS_SX = {
  display: "inline-block",
  ml: "3px",
  "@keyframes acceptDotBlink": {
    "0%": { opacity: 0 },
    "20%": { opacity: 1 },
    "100%": { opacity: 0 },
  },
  "& span": {
    animation: "acceptDotBlink 1.5s infinite",
    animationFillMode: "forwards",
    opacity: 0,
    ml: "1px",
    fontWeight: 700,
  },
  "& span:nth-of-type(1)": { animationDelay: "0s" },
  "& span:nth-of-type(2)": { animationDelay: "0.3s" },
  "& span:nth-of-type(3)": { animationDelay: "0.6s" },
};

const EYEBROW_SX = {
  color: colors.text.muted,
  fontSize: "0.6rem",
  fontWeight: 700,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  lineHeight: 1,
};

function StatusDots() {
  return (
    <Box component="span" sx={DOTS_SX}>
      <span>.</span>
      <span>.</span>
      <span>.</span>
    </Box>
  );
}

export default function AcceptMatchDialog({
  matchId,
  onAcceptMatch,
  timeoutSeconds = 30,
  matchAcceptDialogState,
}) {
  const [open, setOpen] = React.useState(true);
  const [minimized, setMinimized] = React.useState(false);
  const [timeLeft, setTimeLeft] = React.useState(timeoutSeconds);
  const [accepted, setAccepted] = React.useState(
    Boolean(matchAcceptDialogState?.accepted)
  );

  const mapId = matchAcceptDialogState?.map || null;
  const mapVotePending = Boolean(matchAcceptDialogState?.mapVotePending);
  const mapMeta = getMapMetaById(mapId);
  const mapImageUrl = getMapImageUrl(mapId, utils.getEdgeBasePath());

  const totalPlayers = matchAcceptDialogState?.totalPlayers || 2;
  const acceptedCount = matchAcceptDialogState?.acceptedCount || 0;
  const allAccepted = Boolean(matchAcceptDialogState?.allAccepted);

  // If a new match arrives while the previous dialog is mounted (or was auto-closed),
  // reset local UI state so the modal reliably shows for the new match.
  React.useEffect(() => {
    setOpen(true);
    setMinimized(false);
    setTimeLeft(timeoutSeconds);
    setAccepted(Boolean(matchAcceptDialogState?.accepted));
  }, [matchId, timeoutSeconds]);

  React.useEffect(() => {
    if (matchAcceptDialogState?.accepted) {
      setAccepted(true);
    }
  }, [matchAcceptDialogState?.accepted]);

  // Ensure dialog is open (not minimized) when all players accept
  React.useEffect(() => {
    if (allAccepted && minimized) {
      setMinimized(false);
      setOpen(true);
    }
  }, [allAccepted, minimized]);

  // Countdown timer — keeps counting while minimized
  React.useEffect(() => {
    if (timeLeft <= 0) return;

    const timer = setTimeout(() => {
      setTimeLeft((prevTime) => prevTime - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [timeLeft]);

  // Auto-close when timer hits zero. If all players accepted we're waiting for
  // the match to be ready, so don't close on timeout.
  React.useEffect(() => {
    if (timeLeft <= 0 && (open || minimized) && !allAccepted) {
      setOpen(false);
      setMinimized(false);
    }
  }, [timeLeft, open, minimized, allAccepted]);

  const handleClose = () => {
    // Instead of closing, just minimize the dialog
    setOpen(false);
    setMinimized(true);
  };

  const handleReopen = () => {
    setMinimized(false);
    setOpen(true);
  };

  const handleAccept = () => {
    setAccepted(true);
    onAcceptMatch(matchId);
  };

  const urgencyThreshold = Math.ceil(timeoutSeconds / 3);
  const isUrgent = timeLeft <= urgencyThreshold && timeLeft > 0;
  const timerColor = isUrgent ? colors.state.danger.main : colors.primary.main;
  const slotColumns = Math.min(Math.max(totalPlayers, 1), 5);
  const mapLabel = mapVotePending
    ? MAP_VOTE_PENDING_HEADING
    : mapMeta?.display || mapId;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        fullWidth={false}
        slotProps={{ backdrop: { sx: backdropStyles } }}
        sx={{
          zIndex: 1301,
          "& .MuiDialog-container": {
            alignItems: "center",
            justifyContent: "center",
          },
          "& .MuiDialog-paper": {
            m: 0,
            width: { xs: "calc(100vw - 2rem)", sm: "21.5rem", xl: "23.5rem" },
            maxWidth: "calc(100vw - 2rem)",
            overflow: "hidden",
            borderRadius: "16px",
            background: colors.gradient.secondary,
            border: `1px solid ${colors.border.default}`,
            boxShadow: "0 24px 60px rgba(0, 0, 0, 0.5)",
          },
        }}
      >
        <Box
          sx={{
            height: "3px",
            width: "100%",
            backgroundColor: alpha("#fff", 0.06),
          }}
        >
          <Box
            sx={{
              height: "100%",
              width: `${Math.max(0, (timeLeft / timeoutSeconds) * 100)}%`,
              backgroundColor: timerColor,
              boxShadow: `0 0 8px ${alpha(timerColor, 0.5)}`,
              transition: "width 1s linear, background-color 0.3s ease-in-out",
            }}
          />
        </Box>

        <DialogContent sx={{ p: "1.15rem" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "0.7rem",
              mb: "0.9rem",
            }}
          >
            <Box
              sx={{
                width: "2.2rem",
                height: "2.2rem",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "10px",
                backgroundColor: colors.accent.subtle,
              }}
            >
              <SportsEsportsIcon
                sx={{ fontSize: "1.15rem", color: colors.accent.main }}
              />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  color: colors.text.primary,
                  fontSize: "1rem",
                  fontWeight: 600,
                  lineHeight: 1.2,
                }}
              >
                Match Found
              </Typography>
              <Typography
                sx={{
                  color: colors.text.secondary,
                  fontSize: "0.72rem",
                  lineHeight: 1.3,
                }}
              >
                {mapVotePending
                  ? MAP_VOTE_PENDING_HINT
                  : "Accept to join the game."}
              </Typography>
            </Box>

            <Typography
              sx={{
                flexShrink: 0,
                px: "0.5rem",
                py: "0.25rem",
                borderRadius: "8px",
                color: timerColor,
                fontSize: "0.8rem",
                fontWeight: 700,
                fontVariantNumeric: "tabular-nums",
                backgroundColor: alpha(timerColor, 0.1),
                border: `1px solid ${alpha(timerColor, 0.22)}`,
                transition: "color 0.3s ease-in-out",
              }}
            >
              {timeLeft}s
            </Typography>
          </Box>

          {mapVotePending ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "0.55rem",
                px: "0.7rem",
                py: "0.6rem",
                mb: "0.7rem",
                borderRadius: "12px",
                backgroundColor: colors.background.surface,
                border: `1px solid ${colors.border.default}`,
              }}
            >
              <HowToVoteIcon
                sx={{ fontSize: "1.05rem", color: colors.primary.main }}
              />
              <Typography sx={EYEBROW_SX}>Up next</Typography>
              <Typography
                sx={{
                  color: colors.text.primary,
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  lineHeight: 1,
                }}
              >
                {MAP_VOTE_PENDING_HEADING}
              </Typography>
            </Box>
          ) : mapId ? (
            <Box
              sx={{
                position: "relative",
                height: "4.5rem",
                mb: "0.7rem",
                borderRadius: "12px",
                overflow: "hidden",
                border: `1px solid ${colors.border.default}`,
                backgroundColor: colors.background.surface,
                backgroundImage: mapImageUrl ? `url(${mapImageUrl})` : "none",
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-end",
                  gap: "0.2rem",
                  p: "0.6rem",
                  background:
                    "linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.8) 100%)",
                }}
              >
                <Typography sx={EYEBROW_SX}>Map</Typography>
                <Typography
                  sx={{
                    color: colors.text.primary,
                    fontSize: "1rem",
                    fontWeight: 700,
                    lineHeight: 1,
                    textShadow: "0 2px 10px rgba(0,0,0,0.7)",
                  }}
                >
                  {mapLabel}
                </Typography>
              </Box>
            </Box>
          ) : null}

          <Box
            sx={{
              p: "0.7rem",
              borderRadius: "12px",
              backgroundColor: alpha(colors.background.surface, 0.5),
              border: `1px solid ${colors.border.default}`,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "0.5rem",
                mb: "0.55rem",
              }}
            >
              <Typography sx={EYEBROW_SX}>Squad</Typography>
              <Typography
                sx={{
                  color: colors.primary.main,
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  lineHeight: 1,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {acceptedCount}/{totalPlayers} ready
              </Typography>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `repeat(${slotColumns}, minmax(0, 1fr))`,
                gap: "0.35rem",
              }}
            >
              {Array.from({ length: totalPlayers }).map((_, index) => {
                const isSlotAccepted = index < acceptedCount;

                return (
                  <Box
                    key={index}
                    sx={{
                      height: "1.85rem",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition:
                        "background-color 0.15s ease-in-out, border-color 0.15s ease-in-out",
                      backgroundColor: isSlotAccepted
                        ? alpha(colors.primary.main, 0.14)
                        : alpha("#fff", 0.025),
                      border: `1px solid ${
                        isSlotAccepted
                          ? alpha(colors.primary.main, 0.32)
                          : colors.border.subtle
                      }`,
                    }}
                  >
                    {isSlotAccepted ? (
                      <CheckRoundedIcon
                        sx={{ fontSize: "0.95rem", color: colors.primary.main }}
                      />
                    ) : (
                      <PersonIcon
                        sx={{ fontSize: "0.95rem", color: colors.text.muted }}
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Typography
            sx={{
              mt: "0.7rem",
              textAlign: "center",
              fontSize: "0.75rem",
              fontWeight: allAccepted ? 600 : 400,
              color: allAccepted ? colors.primary.main : colors.text.secondary,
            }}
          >
            {allAccepted
              ? "All players accepted! Starting match"
              : "Waiting for all players to accept"}
            <StatusDots />
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: "1.15rem", pt: 0, pb: "1.15rem" }}>
          <Button
            onClick={handleAccept}
            disabled={accepted}
            fullWidth
            sx={{
              ...DIALOG_ACCEPT_BUTTON_SX,
              py: "0.55rem",
              fontSize: "0.9rem",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
          >
            {accepted ? "Accepted" : "Accept Match"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating chip when dialog is minimized */}
      {minimized && timeLeft > 0 && (
        <Box
          onClick={handleReopen}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleReopen();
            }
          }}
          sx={{
            position: "fixed",
            bottom: "1.5rem",
            right: "1.5rem",
            zIndex: 1300,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "0.6rem",
            px: "0.8rem",
            py: "0.6rem",
            borderRadius: "12px",
            background: colors.gradient.secondary,
            border: `1px solid ${alpha(colors.primary.main, 0.22)}`,
            boxShadow: "0 16px 40px rgba(0, 0, 0, 0.5)",
            transition: "border-color 0.15s ease-in-out",
            "&:hover": {
              borderColor: alpha(colors.primary.main, 0.4),
            },
          }}
        >
          <SportsEsportsIcon
            sx={{ fontSize: "1.2rem", color: colors.accent.main }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: colors.text.primary,
                fontSize: "0.8rem",
                fontWeight: 600,
                lineHeight: 1.2,
              }}
            >
              {accepted ? "Match Accepted" : "Match Found"}
            </Typography>
            <Typography
              sx={{
                color: colors.text.secondary,
                fontSize: "0.68rem",
                lineHeight: 1.2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {accepted ? "Waiting for squad" : "Click to accept"}
              {mapLabel ? ` · ${mapLabel}` : ""}
            </Typography>
          </Box>
          <Typography
            sx={{
              flexShrink: 0,
              px: "0.45rem",
              py: "0.2rem",
              borderRadius: "7px",
              color: timerColor,
              fontSize: "0.75rem",
              fontWeight: 700,
              fontVariantNumeric: "tabular-nums",
              backgroundColor: alpha(timerColor, 0.1),
              border: `1px solid ${alpha(timerColor, 0.22)}`,
            }}
          >
            {timeLeft}s
          </Typography>
        </Box>
      )}
    </>
  );
}
